/**
 * One version of an integration's settings, as the screens edit it.
 *
 * The same shape the server reads (`omnium_core/integrations/settings.py`), in
 * camelCase. Every part has a default, so a half-finished draft still loads.
 * The server computes every preview; the helpers here only name and summarise
 * what a person set.
 */

export type ConditionOp =
  | "empty"
  | "not_empty"
  | "equals"
  | "one_of"
  | "contains"
  | "starts_with"
  | "more_than"
  | "less_than"
  | "between"
  | "before"
  | "after"
  | "is_true"
  | "is_false";

export type Condition = {
  column: string;
  op: ConditionOp;
  value?: unknown;
  values?: unknown[];
};

export type FromKind = "column" | "first" | "fixed" | "join";

export type From = {
  kind: FromKind;
  column?: string | null;
  columns?: string[];
  value?: unknown;
  template?: string | null;
};

export type StepKind =
  | "trim"
  | "strip"
  | "replace"
  | "case"
  | "part"
  | "number"
  | "boolean"
  | "datetime"
  | "duration"
  | "map"
  | "default"
  | "lookup"
  | "join"
  | "pick"
  | "round";

export type Step = {
  kind: StepKind;
  chars?: string | null;
  at?: "start" | "end" | "both";
  find?: string | null;
  replaceWith?: string;
  case?: "upper" | "lower" | "title";
  separator?: string;
  which?: "first" | "last" | "nth";
  index?: number;
  format?: string | null;
  values?: Record<string, unknown>;
  otherwise?: "keep" | "default" | "empty" | "hold";
  default?: unknown;
  entity?: "sport" | "participant" | "venue" | "fixture" | null;
  idSource?: string | null;
  column?: string | null;
  equals?: unknown;
  read?: string | null;
  decimals?: number;
};

export type FieldSetting = { from: From; steps: Step[] };

export type Settings = {
  source: { jobId: string | null; runs: "passed" | "omnium" };
  rows: { listPath: string | null; keepParent: boolean };
  keep: { mode: "all" | "any"; conditions: Condition[] };
  fields: Record<string, FieldSetting>;
  match: { idSource: string | null; matchBy: string; keyFields: string[] };
  safety: {
    minShare: number;
    maxNotPlaced: number;
    maxRefused: number;
    writeFrom: string | null;
    writeUntil: string | null;
  };
};

export function emptySettings(): Settings {
  return {
    source: { jobId: null, runs: "passed" },
    rows: { listPath: null, keepParent: true },
    keep: { mode: "all", conditions: [] },
    fields: {},
    match: { idSource: null, matchBy: "externalId", keyFields: ["externalId"] },
    safety: { minShare: 0.5, maxNotPlaced: 0.1, maxRefused: 0.1, writeFrom: null, writeUntil: null },
  };
}

type Loose = Record<string, unknown>;

function obj(value: unknown): Loose {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Loose) : {};
}

/** Settings as stored, with every missing part filled in. */
export function normaliseSettings(raw: unknown): Settings {
  const base = emptySettings();
  const given = obj(raw);
  const fields: Record<string, FieldSetting> = {};
  for (const [key, setting] of Object.entries(obj(given.fields))) {
    const one = obj(setting);
    const from = obj(one.from);
    fields[key] = {
      from: {
        kind: (from.kind as FromKind) ?? "column",
        column: (from.column as string | null | undefined) ?? null,
        columns: Array.isArray(from.columns) ? (from.columns as string[]) : [],
        value: from.value ?? null,
        template: (from.template as string | null | undefined) ?? null,
      },
      steps: Array.isArray(one.steps) ? (one.steps as Step[]) : [],
    };
  }
  return {
    source: { ...base.source, ...(obj(given.source) as Partial<Settings["source"]>) },
    rows: { ...base.rows, ...(obj(given.rows) as Partial<Settings["rows"]>) },
    keep: { ...base.keep, ...(obj(given.keep) as Partial<Settings["keep"]>) },
    fields,
    match: { ...base.match, ...(obj(given.match) as Partial<Settings["match"]>) },
    safety: { ...base.safety, ...(obj(given.safety) as Partial<Settings["safety"]>) },
  };
}

// --------------------------------------------------------------------------- words

export const STEP_KINDS: { kind: StepKind; label: string; note: string }[] = [
  { kind: "trim", label: "Trim spaces", note: "Removes spaces at both ends." },
  { kind: "strip", label: "Remove at start or end", note: "Removes the characters you give." },
  { kind: "replace", label: "Replace text", note: "Replaces one text with another." },
  { kind: "case", label: "Change case", note: "UPPER, lower or Title." },
  { kind: "part", label: "Take part", note: "Split by a character and keep one part." },
  { kind: "number", label: "Read a number", note: "Reads numbers with units or a decimal comma." },
  { kind: "boolean", label: "Read yes / no", note: "Y / N, yes / no, 1 / 0, true / false." },
  { kind: "datetime", label: "Read a date and time", note: "With no time zone, the event's is used." },
  { kind: "duration", label: "Read a duration", note: "m:ss.ff and similar, into seconds." },
  { kind: "map", label: "Map values", note: "Their word to our value." },
  { kind: "default", label: "Default when empty", note: "Uses a value when nothing came." },
  { kind: "lookup", label: "Look up a record", note: "Their code to our record, through id links." },
  { kind: "join", label: "Join a list", note: "A list into one text with a separator." },
  { kind: "pick", label: "Pick from a list", note: "First, last, or where a column equals a value." },
  { kind: "round", label: "Round", note: "To a number of decimals." },
];

export function stepLabel(kind: StepKind): string {
  return STEP_KINDS.find((one) => one.kind === kind)?.label ?? kind;
}

export function newStep(kind: StepKind): Step {
  switch (kind) {
    case "strip":
      return { kind, chars: ". ", at: "start" };
    case "replace":
      return { kind, find: "", replaceWith: "" };
    case "case":
      return { kind, case: "title" };
    case "part":
      return { kind, separator: "-", which: "first", index: 0 };
    case "map":
      return { kind, values: {}, otherwise: "keep", default: null };
    case "default":
      return { kind, default: "" };
    case "lookup":
      return { kind, entity: "sport", idSource: null };
    case "join":
      return { kind, separator: " / " };
    case "pick":
      return { kind, which: "first", column: null, equals: null, read: null };
    case "round":
      return { kind, decimals: 0 };
    default:
      return { kind };
  }
}

function quoted(text: unknown): string {
  return `“${String(text ?? "")}”`;
}

/** A step on a chip: its name and the one setting that tells two apart. */
export function stepSummary(step: Step): string {
  const label = stepLabel(step.kind);
  switch (step.kind) {
    case "strip":
      return `${label}: ${quoted(step.chars ?? " ")} at the ${step.at === "both" || !step.at ? "start and end" : step.at}`;
    case "replace":
      return `${label}: ${quoted(step.find)} → ${quoted(step.replaceWith)}`;
    case "case":
      return `${label}: ${step.case ?? "upper"}`;
    case "part":
      return `${label}: ${step.which === "nth" ? `part ${(step.index ?? 0) + 1}` : (step.which ?? "first")} by ${quoted(step.separator ?? " ")}`;
    case "datetime":
      return step.format ? `${label}: ${step.format}` : label;
    case "map": {
      const count = Object.keys(step.values ?? {}).length;
      return `${label} · ${count}`;
    }
    case "default":
      return `${label}: ${quoted(step.default)}`;
    case "lookup":
      return `${label}: ${step.entity ?? "?"}${step.idSource ? ` (${step.idSource})` : ""}`;
    case "join":
      return `${label}: ${quoted(step.separator ?? " ")}`;
    case "pick":
      return step.column ? `${label}: where ${step.column} = ${quoted(step.equals)}` : `${label}: ${step.which ?? "first"}`;
    case "round":
      return `${label}: ${step.decimals ?? 0} decimals`;
    default:
      return label;
  }
}

export const CONDITION_OPS: { op: ConditionOp; label: string; takes: "none" | "one" | "many" | "two" }[] = [
  { op: "empty", label: "is empty", takes: "none" },
  { op: "not_empty", label: "is not empty", takes: "none" },
  { op: "equals", label: "equals", takes: "one" },
  { op: "one_of", label: "is one of", takes: "many" },
  { op: "contains", label: "contains", takes: "one" },
  { op: "starts_with", label: "starts with", takes: "one" },
  { op: "more_than", label: "is more than", takes: "one" },
  { op: "less_than", label: "is less than", takes: "one" },
  { op: "between", label: "is between", takes: "two" },
  { op: "before", label: "is before", takes: "one" },
  { op: "after", label: "is after", takes: "one" },
  { op: "is_true", label: "is true", takes: "none" },
  { op: "is_false", label: "is false", takes: "none" },
];

export function conditionSentence(condition: Condition): string {
  const found = CONDITION_OPS.find((one) => one.op === condition.op);
  const label = found?.label ?? condition.op;
  const column = condition.column || "(no column)";
  switch (found?.takes) {
    case "none":
      return `${column} ${label}`;
    case "many":
      return `${column} ${label} ${(condition.values ?? []).map(String).join(", ") || "…"}`;
    case "two": {
      const [low, high] = condition.values ?? [];
      return `${column} ${label} ${String(low ?? "…")} and ${String(high ?? "…")}`;
    }
    default:
      return `${column} ${label} ${String(condition.value ?? "…")}`;
  }
}

/** Where a field's value comes from, in a few words. */
export function fromSummary(from: From | undefined): string {
  if (!from) return "(not mapped)";
  switch (from.kind) {
    case "fixed":
      return `Always ${quoted(from.value)}`;
    case "first":
      return (from.columns ?? []).length ? `First of ${(from.columns ?? []).join(", ")}` : "(no columns)";
    case "join":
      return from.template || "(no text)";
    default:
      return from.column || "(not mapped)";
  }
}

export function isMapped(setting: FieldSetting | undefined): boolean {
  if (!setting) return false;
  const from = setting.from;
  if (from.kind === "fixed") return from.value !== null && from.value !== undefined && from.value !== "";
  if (from.kind === "first") return (from.columns ?? []).length > 0;
  if (from.kind === "join") return Boolean(from.template);
  return Boolean(from.column);
}

// --------------------------------------------------------------------------- values

/** A dotted path into a value: `home.id`, `Count.ME_GOLD.total`. */
export function readPath(value: unknown, path: string): unknown {
  let current: unknown = value;
  for (const part of path.split(".")) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== "object") return undefined;
    current = (current as Loose)[part];
  }
  return current;
}

export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

/** Text a person typed, as the field's type wants it. */
export function valueForType(text: string, type: string | undefined): unknown {
  const trimmed = text.trim();
  if (type === "number" || type === "integer") {
    if (trimmed === "") return null;
    const number = Number(trimmed);
    return Number.isFinite(number) ? number : text;
  }
  if (type === "boolean") {
    if (/^(true|yes|y|1)$/i.test(trimmed)) return true;
    if (/^(false|no|n|0)$/i.test(trimmed)) return false;
    return trimmed === "" ? null : text;
  }
  return text;
}

/** A value on screen: short, and never "[object Object]". */
export function showValue(value: unknown, max = 80): string {
  if (value === null || value === undefined) return "—";
  if (value === "") return "(empty)";
  if (typeof value === "boolean") return value ? "yes" : "no";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** How many of these mapped rows have a value for `key`. */
export function filledCount(values: unknown[], key: string): number {
  return values.filter((value) => !isEmptyValue(readPath(value, key))).length;
}

/** Every value seen for `key` across rows, most common first. */
export function valuesSeen(values: unknown[], key: string): { value: string; rows: number }[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    const found = readPath(value, key);
    const word = found === null || found === undefined ? "" : typeof found === "string" ? found : JSON.stringify(found);
    counts.set(word, (counts.get(word) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, rows]) => ({ value, rows }))
    .sort((a, b) => b.rows - a.rows || a.value.localeCompare(b.value));
}

/** Values seen that a map step does not know yet. */
export function unmappedValues(step: Step, seen: { value: string; rows: number }[]): { value: string; rows: number }[] {
  const known = step.values ?? {};
  return seen.filter((one) => !(one.value in known));
}

// --------------------------------------------------------------------------- columns

export type ColumnInfo = { path: string; filled: number; samples: unknown[] };

export type ColumnNode = {
  name: string;
  path: string;
  column: ColumnInfo | null;
  children: ColumnNode[];
};

/**
 * Columns as a tree: `Home.Reg` sits under `Home`, `Inscriptions[].EvKey` under
 * `Inscriptions[]`. Order is first seen.
 */
export function columnTree(columns: ColumnInfo[]): ColumnNode[] {
  const root: ColumnNode = { name: "", path: "", column: null, children: [] };
  for (const column of columns) {
    const parts = column.path.replace(/\[\]\./g, "[].").split(".");
    let node = root;
    let path = "";
    for (const part of parts) {
      path = path ? `${path}.${part}` : part;
      let child = node.children.find((one) => one.name === part);
      if (!child) {
        child = { name: part, path, column: null, children: [] };
        node.children.push(child);
      }
      node = child;
    }
    node.column = column;
  }
  return root.children;
}

/** Columns that are lists: where "one row is each entry" can point. */
export function listColumns(columns: ColumnInfo[]): string[] {
  const out = new Set<string>();
  for (const column of columns) {
    const at = column.path.indexOf("[]");
    if (at > 0) out.add(column.path.slice(0, at + 2));
  }
  return [...out];
}

/** A column path picked for a list field, e.g. `Inscriptions[].EvKey`. */
export function isListPath(path: string): boolean {
  return path.includes("[]");
}
